import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import Seo, { serviceJsonLd } from "@/components/site/Seo";
import { SectionTag } from "@/components/site/Reveal";
import { serviceIcons } from "@/i18n/content";
import { PROJECT_IMAGES, PROJECT_VIDEOS } from "@/i18n/assets";
import { fetchSettings, toAbsolute } from "@/lib/api";
import { useSite } from "@/context/SiteContentContext";
import ShareButtons from "@/components/site/ShareButtons";

const iconMap = [0, 2, 1, 3, 5, 6, 7, 4];

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang, dir } = useLang();
  const { settings: siteSettings } = useSite();
  const idx = parseInt(slug, 10);
  const projectDefault = t.projects.items[idx];
  const detailDefault = t.projects.details?.[idx];
  const project = siteSettings[`project_${idx}_title_${lang}`] || projectDefault;
  const overview = siteSettings[`project_${idx}_overview_${lang}`] || detailDefault?.overview;
  const detail = detailDefault ? { ...detailDefault, overview } : null;
  const coverImg = toAbsolute(siteSettings[`project_${idx}_image`]) || PROJECT_IMAGES[idx];
  const screenshotsRaw = siteSettings[`project_${idx}_screenshots`] || "";
  const screenshots = screenshotsRaw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => toAbsolute(s));
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [videoUrl, setVideoUrl] = useState(PROJECT_VIDEOS[idx] || PROJECT_VIDEOS[0]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    fetchSettings().then((r) => {
      const override = r.data?.[`project_video_${idx}`];
      if (override) setVideoUrl(toAbsolute(override));
    }).catch(() => {});
  }, [idx]);

  if (!project || Number.isNaN(idx)) {
    return (
      <SiteLayout>
        <Seo title="404" />
        <div className="min-h-[55vh] grid place-items-center px-5 text-center" data-testid="project-not-found">
          <div>
            <h1 className="text-5xl font-extrabold mb-3">404</h1>
            <p className="text-slate-400 mb-6">{t.projectDetail.back}</p>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>
              {t.legal.backHome}
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const Icon = serviceIcons[iconMap[idx]] || serviceIcons[0];

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const goContact = () => {
    navigate("/");
    setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 300);
  };

  return (
    <SiteLayout>
      <Seo
        title={project}
        description={detail?.overview || ""}
        jsonLd={serviceJsonLd({
          name: project,
          description: detail?.overview || "",
          image: coverImg,
          url: typeof window !== "undefined" ? window.location.href : "",
        })}
        jsonLdId="seo-jsonld-service"
      />
      <article className="pb-20" dir="auto" data-testid="project-detail">
        {/* Hero video */}
        <div className="relative">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-24 sm:pt-28">
            <Link to="/#projects" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-primary mb-5" data-testid="project-back-link">
              <Arrow size={16} /> {t.projectDetail.back}
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-2xl grid place-items-center" style={{ background: "rgba(0,242,96,0.15)" }}>
                <Icon size={22} className="text-primary" />
              </div>
              <SectionTag>{t.projectDetail.watchIntro}</SectionTag>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-6" data-testid="project-title">{project}</h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,242,96,0.4)]"
              data-testid="project-video-frame"
            >
              <div className="absolute -inset-1 opacity-40 pointer-events-none" style={{ background: "conic-gradient(from 0deg, rgba(0,242,96,0.35), transparent 40%, rgba(0,242,96,0.35))", filter: "blur(40px)" }} />
              <div className="relative aspect-video bg-black">
                <video
                  ref={videoRef}
                  src={videoUrl}
                  poster={coverImg}
                  className="absolute inset-0 h-full w-full object-cover"
                  playsInline
                  muted={muted}
                  loop
                  preload="metadata"
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  data-testid="project-video"
                />

                <button
                  onClick={togglePlay}
                  className="absolute inset-0 grid place-items-center group"
                  data-testid="project-video-play"
                  aria-label="play"
                >
                  <motion.span
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    className={`h-20 w-20 rounded-full grid place-items-center text-[#04120a] transition-opacity ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}
                    style={{ background: "var(--primary)", boxShadow: "0 20px 60px -10px rgba(0,242,96,0.6)" }}
                  >
                    {playing ? <Pause size={30} /> : <Play size={30} className="ml-1" />}
                  </motion.span>
                </button>
                <button
                  onClick={toggleMute}
                  className="absolute bottom-4 right-4 h-11 w-11 rounded-full grid place-items-center bg-black/60 backdrop-blur border border-white/10 hover:border-primary transition-colors"
                  data-testid="project-video-mute"
                  aria-label="mute"
                >
                  {muted ? <VolumeX size={18} className="text-white" /> : <Volume2 size={18} className="text-primary" />}
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Overview + Features */}
        <div className="mx-auto max-w-6xl px-5 sm:px-8 mt-14">
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              <SectionTag>{t.projectDetail.overview}</SectionTag>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mt-5" data-testid="project-overview">
                {detail?.overview}
              </p>
            </div>
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-extrabold mb-5">{t.projectDetail.features}</h2>
              <div className="space-y-3">
                {(detail?.features || []).map((f, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: dir === "rtl" ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="card-surface rounded-xl p-4 flex items-center gap-3"
                    data-testid={`project-feature-${i}`}
                  >
                    <CheckCircle2 size={20} className="text-primary shrink-0" />
                    <span className="font-medium text-sm sm:text-base">{f}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Screenshots gallery */}
          {screenshots.length > 0 && (
            <div className="mt-14" data-testid="project-screenshots">
              <SectionTag>{t.projectDetail.screenshots}</SectionTag>
              <h3 className="text-2xl sm:text-3xl font-extrabold mt-4 mb-6">{t.projectDetail.screenshotsTitle}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {screenshots.map((src, i) => (
                  <motion.a
                    key={i}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -4, scale: 1.02 }}
                    className="group relative aspect-video rounded-2xl overflow-hidden border border-white/10 block"
                    data-testid={`project-screenshot-${i}`}
                  >
                    <img src={src} alt={`${project} screenshot ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.a>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="card-surface rounded-3xl p-8 sm:p-10 text-center mt-14">
            <h3 className="text-2xl sm:text-3xl font-extrabold mb-3">{project}</h3>
            <p className="text-slate-400 mb-7 max-w-xl mx-auto">{t.contact.subtitle}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={goContact} className="rounded-full px-7 py-3.5 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }} data-testid="project-cta">
                {t.projectDetail.cta}
              </button>
              <Link to="/#projects" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold border border-white/15 hover:bg-white/5 transition-colors">
                <Arrow size={16} /> {t.projectDetail.back}
              </Link>
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 flex justify-center">
              <ShareButtons title={project} testid="project-share" shareType="projects" shareId={idx} />
            </div>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}
